import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Edit, Trash2, Upload, Download, Save, X, Search, MapPin } from "lucide-react";

const blankAddress = { label: "", address: "", place_of_supply: "" };
const blank = {
  name: "",
  gst: "",
  vendor_code: "",
  place_of_supply: "",
  addresses: [{ ...blankAddress }],
};

export default function CustomerMaster() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "gm";
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const reload = async () => {
    const { data } = await api.get("/customers");
    setRows(data || []);
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      [r.name, r.address, r.gst, r.vendor_code, r.place_of_supply,
       ...((r.addresses || []).map((a) => a.address))]
        .some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [rows, search]);

  const openAdd = () => { setEditing("new"); setForm({ ...blank, addresses: [{ ...blankAddress }] }); };
  const openEdit = (r) => {
    setEditing(r.id);
    const addrList = Array.isArray(r.addresses) && r.addresses.length
      ? r.addresses
      : (r.address ? [{ label: "Primary", address: r.address, place_of_supply: r.place_of_supply || "" }] : [{ ...blankAddress }]);
    setForm({
      name: r.name || "",
      gst: r.gst || "",
      vendor_code: r.vendor_code || "",
      place_of_supply: r.place_of_supply || "",
      addresses: addrList.map((a) => ({ label: a.label || "", address: a.address || "", place_of_supply: a.place_of_supply || "" })),
    });
  };
  const cancel = () => { setEditing(null); setForm(blank); };

  const addAddress = () => setForm((f) => ({ ...f, addresses: [...f.addresses, { ...blankAddress }] }));
  const removeAddress = (idx) => setForm((f) => ({
    ...f,
    addresses: f.addresses.length <= 1 ? [{ ...blankAddress }] : f.addresses.filter((_, i) => i !== idx),
  }));
  const updateAddress = (idx, patch) => setForm((f) => ({
    ...f,
    addresses: f.addresses.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
  }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Customer name required");
    // Drop empty rows
    const cleanAddrs = form.addresses
      .map((a) => ({ label: (a.label || "").trim(), address: (a.address || "").trim(), place_of_supply: (a.place_of_supply || "").trim() }))
      .filter((a) => a.address);
    const payload = {
      name: form.name.trim(),
      gst: form.gst.trim(),
      vendor_code: form.vendor_code.trim(),
      place_of_supply: form.place_of_supply.trim(),
      address: cleanAddrs[0]?.address || "",
      addresses: cleanAddrs,
    };
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/customers", payload);
        toast.success("Customer added");
      } else {
        await api.put(`/customers/${editing}`, payload);
        toast.success("Customer updated");
      }
      cancel();
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete customer "${r.name}"?\nThis is blocked if any Sales PO references it.`)) return;
    try {
      await api.delete(`/customers/${r.id}`);
      toast.success("Customer deleted");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("pms_token");
    const r = await fetch(`${API}/customers/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "customer_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const t = toast.loading("Importing customers…");
    try {
      const { data } = await api.post("/customers/import", fd);
      toast.success(`Imported ${data.inserted} · skipped ${data.skipped}`, { id: t });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed", { id: t });
    }
  };

  const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Masters</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Customer Master</h1>
          <p className="text-sm text-slate-500 mt-1">Name, multi-site addresses, GST &amp; vendor code — picked from the dropdown in Sales Management and printed on the Tax Invoice.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={downloadTemplate} data-testid="customer-template-btn" className="bg-white border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2">
            <Download size={14} /> Template
          </button>
          {canManage && (
            <>
              <input type="file" accept=".xlsx" ref={fileRef} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) { onImport(f); e.target.value = ""; } }} />
              <button onClick={() => fileRef.current?.click()} data-testid="customer-import-btn" className="bg-white border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2">
                <Upload size={14} /> Import Excel
              </button>
              <button onClick={openAdd} data-testid="customer-add-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-3 py-2 rounded-md text-sm inline-flex items-center gap-2 font-semibold">
                <Plus size={14} /> Add
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customer / GST / vendor code / address…"
          className={`${inputCls} pl-9`} data-testid="customer-search" />
      </div>

      {/* Add/Edit drawer */}
      {editing && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="customer-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Customer Name *">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="ACME Industries" data-testid="customer-name-input" autoFocus />
            </Field>
            <Field label="GST Number">
              <input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} className={`${inputCls} font-mono`} placeholder="27AAQFT4025L1ZP" data-testid="customer-gst-input" />
            </Field>
            <Field label="Vendor Code">
              <input value={form.vendor_code} onChange={(e) => setForm({ ...form, vendor_code: e.target.value })} className={`${inputCls} font-mono`} placeholder="VC-ACME-001" data-testid="customer-vendor-code-input" />
            </Field>
            <Field label="Default Place of Supply">
              <input value={form.place_of_supply} onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })} className={inputCls} placeholder="Maharashtra (27)" data-testid="customer-pos-input" />
            </Field>
          </div>

          {/* Addresses list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1.5">
                <MapPin size={12} /> Addresses ({form.addresses.length})
              </div>
              <button onClick={addAddress} data-testid="customer-add-address-btn" type="button"
                className="text-xs px-2.5 py-1 rounded-md border border-emerald-500 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1.5 font-semibold">
                <Plus size={12} /> Add address
              </button>
            </div>
            <div className="space-y-2">
              {form.addresses.map((a, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start border border-slate-200 rounded-md p-3 bg-slate-50/40">
                  <div className="col-span-12 sm:col-span-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Label</div>
                    <input value={a.label} onChange={(e) => updateAddress(i, { label: e.target.value })}
                      className={inputCls} placeholder="Head Office / Pune Plant" data-testid={`customer-addr-label-${i}`} />
                  </div>
                  <div className="col-span-12 sm:col-span-6">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Address</div>
                    <textarea value={a.address} onChange={(e) => updateAddress(i, { address: e.target.value })}
                      className={`${inputCls} min-h-[60px]`} placeholder="Plot 12, MIDC, Pune 411026" data-testid={`customer-addr-text-${i}`} />
                  </div>
                  <div className="col-span-10 sm:col-span-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Place of Supply</div>
                    <input value={a.place_of_supply} onChange={(e) => updateAddress(i, { place_of_supply: e.target.value })}
                      className={inputCls} placeholder="Maharashtra (27)" data-testid={`customer-addr-pos-${i}`} />
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex justify-end">
                    <button onClick={() => removeAddress(i)} type="button" title="Remove"
                      className="mt-5 p-1.5 text-rose-600 hover:bg-rose-50 rounded" data-testid={`customer-addr-remove-${i}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-2">
              The first address is treated as the <strong>Primary</strong>. Bill-To / Ship-To dropdowns on Sales Management will list every address you add here.
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={cancel} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md inline-flex items-center gap-1"><X size={14} /> Cancel</button>
            <button onClick={save} disabled={saving} data-testid="customer-save-btn"
              className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white px-4 py-1.5 text-sm rounded-md font-semibold inline-flex items-center gap-2">
              <Save size={14} /> {saving ? "Saving…" : (editing === "new" ? "Add Customer" : "Update")}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="customer-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <Th>Name</Th><Th>GST Number</Th><Th>Vendor Code</Th><Th>Place of Supply</Th><Th>Addresses</Th>
              {canManage && <Th align="right">Actions</Th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center text-slate-400 py-6">No customers yet</td></tr>
            )}
            {filtered.map((r) => {
              const addrs = Array.isArray(r.addresses) && r.addresses.length ? r.addresses : (r.address ? [{ address: r.address }] : []);
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <Td className="font-medium">{r.name}</Td>
                  <Td className="font-mono text-xs">{r.gst || "—"}</Td>
                  <Td className="font-mono text-xs">{r.vendor_code || "—"}</Td>
                  <Td className="text-xs">{r.place_of_supply || "—"}</Td>
                  <Td className="text-xs">
                    {addrs.length === 0 && "—"}
                    {addrs.slice(0, 2).map((a, i) => (
                      <div key={i} className="text-slate-700 mb-0.5">
                        {a.label && <span className="font-semibold text-slate-900">{a.label}: </span>}
                        <span className="text-slate-600">{a.address}</span>
                      </div>
                    ))}
                    {addrs.length > 2 && <div className="text-[10px] text-violet-700 font-semibold">+{addrs.length - 2} more</div>}
                  </Td>
                  {canManage && (
                    <Td align="right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(r)} className="p-1.5 text-[#1D2951] hover:bg-slate-100 rounded" data-testid={`customer-edit-${r.id}`}><Edit size={14} /></button>
                        <button onClick={() => remove(r)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" data-testid={`customer-delete-${r.id}`}><Trash2 size={14} /></button>
                      </div>
                    </Td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{label}</div>
    {children}
  </div>
);
const Th = ({ children, align = "left" }) => (
  <th className={`px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-${align}`}>{children}</th>
);
const Td = ({ children, align = "left", className = "" }) => (
  <td className={`px-3 py-2 text-${align} ${className}`}>{children}</td>
);
